import { lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AuthLayout } from '@/layouts/auth-layout';
import { AppLayout } from '@/layouts/app-layout';
import { RequireAuth } from '@/router/require-auth';
import { RoleGuard } from '@/router/role-guard';
import { LandingPage } from '@/pages/landing-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { LoginPage } from '@/pages/auth/login-page';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password-page';
import type { SystemRoleKey } from '@/types/auth';

/**
 * Route-level code splitting. Every authenticated `/app/*` page ships
 * as its own lazily-loaded chunk instead of being bundled into the
 * single entry file (previously a ~1.1 MB monolith). The shell —
 * layouts, route guards, landing + auth pages — stays eager so first
 * paint and login are immediate; the Suspense boundary in AppLayout
 * renders a spinner while a page chunk downloads on navigation.
 *
 * `lazyPage` wraps a named export so `<Page />` keeps working unchanged
 * (React.lazy expects a `default` export).
 */
function lazyPage<M extends Record<string, unknown>, K extends keyof M>(
  loader: () => Promise<M>,
  name: K,
) {
  return lazy(async () => ({ default: (await loader())[name] as ComponentType }));
}

const DashboardRouter = lazyPage(() => import('@/pages/app/dashboard-router'), 'DashboardRouter');
const ProfilePage = lazyPage(() => import('@/pages/app/profile/profile-page'), 'ProfilePage');
const MfaSetupPage = lazyPage(() => import('@/pages/app/profile/mfa-setup-page'), 'MfaSetupPage');
const TenantsListPage = lazyPage(() => import('@/pages/app/super-admin/tenants-list-page'), 'TenantsListPage');
const TenantDetailPage = lazyPage(() => import('@/pages/app/super-admin/tenant-detail-page'), 'TenantDetailPage');
const CreateTenantPage = lazyPage(() => import('@/pages/app/super-admin/create-tenant-page'), 'CreateTenantPage');
const DepartmentsPage = lazyPage(() => import('@/pages/app/tenant-admin/departments-page'), 'DepartmentsPage');
const FrameworksListPage = lazyPage(() => import('@/pages/app/grc/frameworks/frameworks-list-page'), 'FrameworksListPage');
const ImportFrameworkPage = lazyPage(() => import('@/pages/app/grc/frameworks/import-framework-page'), 'ImportFrameworkPage');
const FrameworkDetailPage = lazyPage(() => import('@/pages/app/grc/frameworks/framework-detail-page'), 'FrameworkDetailPage');
const MappingsPage = lazyPage(() => import('@/pages/app/grc/frameworks/mappings-page'), 'MappingsPage');
const AssessmentsListPage = lazyPage(() => import('@/pages/app/grc/assessments/assessments-list-page'), 'AssessmentsListPage');
const CreateAssessmentPage = lazyPage(() => import('@/pages/app/grc/assessments/create-assessment-page'), 'CreateAssessmentPage');
const AssessmentDetailPage = lazyPage(() => import('@/pages/app/grc/assessments/assessment-detail-page'), 'AssessmentDetailPage');
const TasksPage = lazyPage(() => import('@/pages/app/workspace/tasks-page'), 'TasksPage');
const EvidenceDetailPage = lazyPage(() => import('@/pages/app/grc/evidence/evidence-detail-page'), 'EvidenceDetailPage');
const ObservationsListPage = lazyPage(() => import('@/pages/app/grc/observations/observations-list-page'), 'ObservationsListPage');
const CreateObservationPage = lazyPage(() => import('@/pages/app/grc/observations/create-observation-page'), 'CreateObservationPage');
const ObservationDetailPage = lazyPage(() => import('@/pages/app/grc/observations/observation-detail-page'), 'ObservationDetailPage');
const ReportsDashboardPage = lazyPage(() => import('@/pages/app/reports/reports-dashboard-page'), 'ReportsDashboardPage');
const FrameworkReportPage = lazyPage(() => import('@/pages/app/reports/framework-report-page'), 'FrameworkReportPage');
const IdentityProvidersPage = lazyPage(() => import('@/pages/app/admin/identity-providers-page'), 'IdentityProvidersPage');
const ScimTokensPage = lazyPage(() => import('@/pages/app/admin/scim-tokens-page'), 'ScimTokensPage');
const AuditIntegrityPage = lazyPage(() => import('@/pages/app/admin/audit-integrity-page'), 'AuditIntegrityPage');
const BrandingPage = lazyPage(() => import('@/pages/app/admin/branding-page'), 'BrandingPage');
const WebhooksPage = lazyPage(() => import('@/pages/app/admin/webhooks-page'), 'WebhooksPage');
const FrameworkCataloguePage = lazyPage(() => import('@/pages/app/admin/framework-catalogue-page'), 'FrameworkCataloguePage');
const ApprovalsListPage = lazyPage(() => import('@/pages/app/admin/approvals-page'), 'ApprovalsListPage');
const ApprovalDetailPage = lazyPage(() => import('@/pages/app/admin/approvals-page'), 'ApprovalDetailPage');
const LicensePage = lazyPage(() => import('@/pages/app/super-admin/license-page'), 'LicensePage');
const UsersListPage = lazyPage(() => import('@/pages/app/tenant-admin/users-list-page'), 'UsersListPage');
const UserDetailPage = lazyPage(() => import('@/pages/app/tenant-admin/user-detail-page'), 'UserDetailPage');
const ExecutiveDashboardPage = lazyPage(() => import('@/pages/app/executive/executive-dashboard-page'), 'ExecutiveDashboardPage');
const ManagerTeamPage = lazyPage(() => import('@/pages/app/manager/manager-team-page'), 'ManagerTeamPage');
const TeamMemberDrillDownPage = lazyPage(() => import('@/pages/app/manager/team-member-drill-down-page'), 'TeamMemberDrillDownPage');
const UserActivityPage = lazyPage(() => import('@/pages/app/users/user-activity-page'), 'UserActivityPage');
const ChangeRequestPage = lazyPage(() => import('@/pages/app/manager/change-request-page'), 'ChangeRequestPage');
const MeDashboardPage = lazyPage(() => import('@/pages/app/me-dashboard-page'), 'MeDashboardPage');
const AssignmentPage = lazyPage(() => import('@/pages/app/assignment/assignment-page'), 'AssignmentPage');
const EscalationsPage = lazyPage(() => import('@/pages/app/escalations/escalations-page'), 'EscalationsPage');
const EscalationPolicyPage = lazyPage(() => import('@/pages/app/admin/escalation-policy-page'), 'EscalationPolicyPage');
const AnnualPlanPage = lazyPage(() => import('@/pages/app/annual-plan/annual-plan-page'), 'AnnualPlanPage');
const DepartmentDashboardPage = lazyPage(() => import('@/pages/app/departments/department-dashboard-page'), 'DepartmentDashboardPage');
const SettingsPage = lazyPage(() => import('@/pages/app/settings/settings-page'), 'SettingsPage');
const PoliciesPage = lazyPage(() => import('@/pages/app/policies/policies-page'), 'PoliciesPage');
const RiskRegisterPage = lazyPage(() => import('@/pages/app/risks/risk-register-page'), 'RiskRegisterPage');
const KriPage = lazyPage(() => import('@/pages/app/risks/kri-page'), 'KriPage');
const IncidentsPage = lazyPage(() => import('@/pages/app/risks/incidents-page'), 'IncidentsPage');
const RiskAppetitePage = lazyPage(() => import('@/pages/app/risks/risk-appetite-page'), 'RiskAppetitePage');
const WorkPage = lazyPage(() => import('@/pages/app/work/work-page'), 'WorkPage');
const DepartmentsIndexPage = lazyPage(() => import('@/pages/app/departments/departments-index-page'), 'DepartmentsIndexPage');
const DepartmentManagerHome = lazyPage(() => import('@/pages/app/department-manager-home'), 'DepartmentManagerHome');

const PLATFORM_TYPES = ['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN'] as const;
const TENANT_ADMIN_TYPES = ['TENANT_SYSTEM_ADMIN', 'TENANT_ADMIN'] as const;
const TENANT_ADMIN_ROLES = ['TENANT_ADMIN'] as const satisfies ReadonlyArray<SystemRoleKey>;
const COMPLIANCE_MANAGER_ROLES = ['COMPLIANCE_MANAGER'] as const satisfies ReadonlyArray<SystemRoleKey>;
const TENANT_SETTINGS_ROLES = ['TENANT_ADMIN', 'COMPLIANCE_MANAGER'] as const satisfies ReadonlyArray<SystemRoleKey>;
const TASK_USER_ROLES = ['COMPLIANCE_OFFICER', 'DEPARTMENT_STAFF'] as const satisfies ReadonlyArray<SystemRoleKey>;
const EXECUTIVE_DASHBOARD_ROLES = [
  'COMPLIANCE_MANAGER',
  'AUDITOR_REVIEWER',
  'EXECUTIVE_VIEWER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const RISK_REGISTER_ROLES = [
  'RISK_MANAGER',
  'COMPLIANCE_MANAGER',
  'EXECUTIVE_VIEWER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const KRI_ROLES = [
  'TENANT_ADMIN',
  'RISK_MANAGER',
  'COMPLIANCE_MANAGER',
  'AUDITOR_REVIEWER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const RISK_INCIDENT_ROLES = [
  'TENANT_ADMIN',
  'RISK_MANAGER',
  'COMPLIANCE_MANAGER',
  'AUDITOR_REVIEWER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const RISK_APPETITE_ROLES = ['TENANT_ADMIN', 'RISK_MANAGER'] as const satisfies ReadonlyArray<SystemRoleKey>;
const MANAGER_TEAM_ROLES = [
  'COMPLIANCE_MANAGER',
  'RISK_MANAGER',
  'DEPARTMENT_MANAGER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const AUDIT_ACTIVITY_ROLES = [
  'TENANT_ADMIN',
  'COMPLIANCE_MANAGER',
  'COMPLIANCE_OFFICER',
  'RISK_MANAGER',
  'DEPARTMENT_MANAGER',
  'AUDITOR_REVIEWER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const GRC_READ_ROLES = ['COMPLIANCE_MANAGER', 'AUDITOR_REVIEWER'] as const satisfies ReadonlyArray<SystemRoleKey>;
const EVIDENCE_READ_ROLES = [
  'TENANT_ADMIN',
  'COMPLIANCE_MANAGER',
  'COMPLIANCE_OFFICER',
  'DEPARTMENT_STAFF',
  'AUDITOR_REVIEWER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const ESCALATION_ROLES = [
  'TENANT_ADMIN',
  'COMPLIANCE_MANAGER',
  'COMPLIANCE_OFFICER',
  'DEPARTMENT_MANAGER',
] as const satisfies ReadonlyArray<SystemRoleKey>;
const DEPARTMENT_MANAGER_ROLES = ['DEPARTMENT_MANAGER'] as const satisfies ReadonlyArray<SystemRoleKey>;

export const router = createBrowserRouter([
  { path: '/', element: <LandingPage /> },

  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/auth/login" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
    ],
  },

  {
    path: '/app',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardRouter /> },
      {
        path: 'me',
        element: (
          <RoleGuard allowRoles={TASK_USER_ROLES} fallback="/app">
            <MeDashboardPage />
          </RoleGuard>
        ),
      },
      // Assignment + Annual Plan are compliance surfaces. Risk has
      // a separate register and only receives compliance requests
      // through the shared task queue when the Risk department is
      // explicitly assigned work.
      {
        path: 'assignment',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <AssignmentPage />
          </RoleGuard>
        ),
      },
      {
        path: 'annual-plan',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <AnnualPlanPage />
          </RoleGuard>
        ),
      },
      {
        path: 'departments/:id',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <DepartmentDashboardPage />
          </RoleGuard>
        ),
      },
      {
        path: 'departments-view',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <DepartmentsIndexPage />
          </RoleGuard>
        ),
      },
      {
        path: 'departments-view/:id',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <DepartmentsIndexPage />
          </RoleGuard>
        ),
      },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'profile/mfa', element: <MfaSetupPage /> },

      // Settings shell — role-aware tabbed layout.
      //   CM tabs: Frameworks / Policies / Escalation Policy / Profile.
      //   Admin tabs: SSO / SCIM / Webhooks / Branding / Catalogue /
      //               Escalation Policy / Audit Integrity / Profile.
      // Each tab keeps its own URL so deep-links survive.
      {
        // Settings shell is reachable to EVERY authenticated user.
        // The Profile tab is the universal landing; role-restricted
        // tabs (Frameworks / Policies for CM, SSO / SCIM / Webhooks /
        // Branding / Catalogue / Audit Integrity for Admin) each
        // carry their own RoleGuard with fallback to /app/settings/profile.
        // The legacy parent-level guard previously denied Risk Manager,
        // Officer, Department Manager/Staff, Auditor, and Executive
        // Viewer outright — the gear button in the top nav points
        // here, so every role now lands on a usable Profile view.
        path: 'settings',
        element: <SettingsPage />,
        children: [
          { index: true, element: <Navigate to="/app/settings/profile" replace /> },
          {
            path: 'frameworks',
            element: (
              <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app/settings/profile">
                <FrameworksListPage />
              </RoleGuard>
            ),
          },
          {
            path: 'policies',
            element: (
              <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app/settings/profile">
                <PoliciesPage />
              </RoleGuard>
            ),
          },
          {
            path: 'identity-providers',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app/settings/profile">
                <IdentityProvidersPage />
              </RoleGuard>
            ),
          },
          {
            path: 'scim-tokens',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app/settings/profile">
                <ScimTokensPage />
              </RoleGuard>
            ),
          },
          {
            path: 'webhooks',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app/settings/profile">
                <WebhooksPage />
              </RoleGuard>
            ),
          },
          {
            path: 'branding',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app/settings/profile">
                <BrandingPage />
              </RoleGuard>
            ),
          },
          {
            path: 'framework-catalogue',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app/settings/profile">
                <FrameworkCataloguePage />
              </RoleGuard>
            ),
          },
          {
            path: 'audit-integrity',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app/settings/profile">
                <AuditIntegrityPage />
              </RoleGuard>
            ),
          },
          {
            path: 'escalation-policy',
            element: (
              <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_SETTINGS_ROLES} fallback="/app/settings/profile">
                <EscalationPolicyPage />
              </RoleGuard>
            ),
          },
          // CM → Admin change-request form lives here as a settings
          // tab (moved from /app/manager/change-requests/new). The
          // standalone path below stays as a redirect for any
          // deep-link still pointing at it.
          {
            path: 'change-requests',
            element: (
              <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app/settings/profile">
                <ChangeRequestPage />
              </RoleGuard>
            ),
          },
          // Profile is the universal Settings tab — no RoleGuard.
          // Every role lands here when they click the top-nav gear.
          { path: 'profile', element: <ProfilePage /> },
        ],
      },

      // Work shell — Compliance Manager's combined operational surface
      // for Tasks / Assessments / Observations / Assignment / Annual
      // Plan / Escalations. Each tab keeps its own URL for deep-linking.
      // Other operator roles still reach the standalone pages directly.
      {
        path: 'work',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <WorkPage />
          </RoleGuard>
        ),
        children: [
          { index: true, element: <Navigate to="/app/work/tasks" replace /> },
          { path: 'tasks', element: <TasksPage /> },
          { path: 'assessments', element: <AssessmentsListPage /> },
          { path: 'observations', element: <ObservationsListPage /> },
          { path: 'assignment', element: <AssignmentPage /> },
          { path: 'annual-plan', element: <AnnualPlanPage /> },
          { path: 'escalations', element: <EscalationsPage /> },
        ],
      },

      // Super Admin only.
      {
        path: 'super-admin/tenants',
        element: (
          <RoleGuard allow={PLATFORM_TYPES}>
            <TenantsListPage />
          </RoleGuard>
        ),
      },
      {
        path: 'super-admin/tenants/new',
        element: (
          <RoleGuard allow={PLATFORM_TYPES}>
            <CreateTenantPage />
          </RoleGuard>
        ),
      },
      {
        path: 'super-admin/tenants/:id',
        element: (
          <RoleGuard allow={PLATFORM_TYPES}>
            <TenantDetailPage />
          </RoleGuard>
        ),
      },

      // Tenant Admin.
      {
        path: 'departments',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <DepartmentsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/identity-providers',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <IdentityProvidersPage />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/scim-tokens',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <ScimTokensPage />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/audit-integrity',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES} fallback="/app">
            <AuditIntegrityPage />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/branding',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <BrandingPage />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/webhooks',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <WebhooksPage />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/framework-catalogue',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <FrameworkCataloguePage />
          </RoleGuard>
        ),
      },
      // Escalation policy editor — Tenant Admin reaches it from the
      // admin route; Compliance Manager reaches the same page through
      // the consolidated /app/settings/escalation-policy tab.
      {
        path: 'admin/escalation-policy',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_SETTINGS_ROLES} fallback="/app">
            <EscalationPolicyPage />
          </RoleGuard>
        ),
      },
      // Approvals — Tenant Admin only. The Maker-Checker engine
      // currently guards tenant-admin break-glass actions
      // (tenant.disable_mfa, role.elevate_privileges) so no other
      // role has business on this surface. RoleGuard refuses other
      // roles client-side; the backend policy gates still enforce
      // it independently.
      {
        path: 'approvals',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <ApprovalsListPage />
          </RoleGuard>
        ),
      },
      {
        path: 'approvals/:id',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <ApprovalDetailPage />
          </RoleGuard>
        ),
      },
      // Convenience redirect: the sidebar's Users link lives at
      // /app/tenant-admin/users, but operators sometimes truncate to
      // /app/tenant-admin. Forward instead of 404-ing.
      { path: 'tenant-admin', element: <Navigate to="/app/tenant-admin/users" replace /> },
      {
        path: 'tenant-admin/users',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <UsersListPage />
          </RoleGuard>
        ),
      },
      {
        path: 'tenant-admin/users/:id',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={TENANT_ADMIN_ROLES}>
            <UserDetailPage />
          </RoleGuard>
        ),
      },
      {
        path: 'executive',
        element: (
          // Executive dashboard is for Compliance Manager +
          // Auditor + Executive Viewer. TENANT_ADMIN's home is
          // /app (TenantAdminDashboard) — sending them to the
          // executive view by URL was a back-door around the
          // sidebar trim.
          <RoleGuard allowRoles={EXECUTIVE_DASHBOARD_ROLES} fallback="/app">
            <ExecutiveDashboardPage />
          </RoleGuard>
        ),
      },
      {
        path: 'risks',
        element: (
          <RoleGuard allowRoles={RISK_REGISTER_ROLES} fallback="/app">
            <RiskRegisterPage />
          </RoleGuard>
        ),
      },
      {
        path: 'kris',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={KRI_ROLES} fallback="/app">
            <KriPage />
          </RoleGuard>
        ),
      },
      {
        path: 'incidents',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={RISK_INCIDENT_ROLES} fallback="/app">
            <IncidentsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'risk-appetite',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={RISK_APPETITE_ROLES} fallback="/app">
            <RiskAppetitePage />
          </RoleGuard>
        ),
      },
      {
        path: 'manager/dept-home',
        element: (
          <RoleGuard allowRoles={DEPARTMENT_MANAGER_ROLES} fallback="/app">
            <DepartmentManagerHome />
          </RoleGuard>
        ),
      },
      {
        path: 'manager/team',
        element: (
          <RoleGuard allowRoles={MANAGER_TEAM_ROLES} fallback="/app">
            <ManagerTeamPage />
          </RoleGuard>
        ),
      },
      {
        path: 'manager/team/:userId',
        element: (
          <RoleGuard allowRoles={MANAGER_TEAM_ROLES} fallback="/app">
            <TeamMemberDrillDownPage />
          </RoleGuard>
        ),
      },
      // Per-employee audit log — open to operator/admin roles with
      // audit visibility. Executive Viewer stays limited to the two
      // executive surfaces.
      {
        path: 'users/:userId/activity',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={AUDIT_ACTIVITY_ROLES} fallback="/app">
            <UserActivityPage />
          </RoleGuard>
        ),
      },
      // Legacy path — the form is now a Settings tab. Redirect any
      // Compliance Manager bookmarks to the new canonical location.
      {
        path: 'manager/change-requests/new',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <Navigate to="/app/settings/change-requests" replace />
          </RoleGuard>
        ),
      },
      {
        path: 'super-admin/license',
        element: (
          <RoleGuard allow={PLATFORM_TYPES}>
            <LicensePage />
          </RoleGuard>
        ),
      },

      // GRC operational pages — Compliance Manager owns write
      // surfaces; Auditor gets read-only catalogue/detail pages.
      // Executive Viewer stays limited to the executive dashboard +
      // risk register. Tenant Admin uses framework-catalogue instead.
      {
        path: 'frameworks',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <FrameworksListPage />
          </RoleGuard>
        ),
      },
      {
        path: 'frameworks/import',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <ImportFrameworkPage />
          </RoleGuard>
        ),
      },
      {
        path: 'frameworks/mappings',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <MappingsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'policies',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <PoliciesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'frameworks/:id',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <FrameworkDetailPage />
          </RoleGuard>
        ),
      },
      {
        path: 'assessments',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <AssessmentsListPage />
          </RoleGuard>
        ),
      },
      {
        path: 'assessments/new',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <CreateAssessmentPage />
          </RoleGuard>
        ),
      },
      {
        path: 'assessments/:id',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <AssessmentDetailPage />
          </RoleGuard>
        ),
      },
      // Tasks are the focused queue for Compliance Officer +
      // Department Staff. Compliance Manager uses /app/work/tasks.
      {
        path: 'workspace/tasks',
        element: (
          <RoleGuard allowRoles={TASK_USER_ROLES} fallback="/app">
            <TasksPage />
          </RoleGuard>
        ),
      },
      {
        path: 'escalations',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={ESCALATION_ROLES} fallback="/app">
            <EscalationsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'evidence/:id',
        element: (
          <RoleGuard allow={TENANT_ADMIN_TYPES} allowRoles={EVIDENCE_READ_ROLES} fallback="/app">
            <EvidenceDetailPage />
          </RoleGuard>
        ),
      },
      // Observations + Reports follow the same GRC read/write split:
      // CM manages; Auditor reads.
      {
        path: 'observations',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <ObservationsListPage />
          </RoleGuard>
        ),
      },
      {
        path: 'observations/new',
        element: (
          <RoleGuard allowRoles={COMPLIANCE_MANAGER_ROLES} fallback="/app">
            <CreateObservationPage />
          </RoleGuard>
        ),
      },
      {
        path: 'observations/:id',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <ObservationDetailPage />
          </RoleGuard>
        ),
      },
      {
        path: 'reports',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <ReportsDashboardPage />
          </RoleGuard>
        ),
      },
      {
        path: 'reports/:id',
        element: (
          <RoleGuard allowRoles={GRC_READ_ROLES} fallback="/app">
            <FrameworkReportPage />
          </RoleGuard>
        ),
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
